import React from "react";
import Navbar from "../components/Navbar";
import Footer from "../components/Footer";
import SEO from "../components/SEO";
import { useBookDemo } from "../hooks/useBookDemo";
import { ArrowRight } from "lucide-react";

// Site brand accent — matches CTA / animated word highlight used across the site.
const ACCENT = "#8942FE";

// Shared inline styles that mimic the site's existing typographic system:
//   • Headings: DM Sans, tight tracking, near-black (#0F0F1E)
//   • Body copy: Inter/DM Sans, 17-18px, gray-700
//   • Accent phrases: brand purple, semi-bold
const H = ({ children, size = "lg" }) => {
  const map = {
    xl: "text-[40px] sm:text-[52px] md:text-[60px] font-bold leading-[1.05] tracking-tight",
    lg: "text-[26px] sm:text-[32px] md:text-[38px] font-bold leading-[1.15] tracking-tight",
    md: "text-[20px] sm:text-[24px] font-bold leading-[1.2] tracking-tight",
  };
  return (
    <h2
      className={`text-[#0F0F1E] ${map[size]}`}
      style={{ fontFamily: "'DM Sans', sans-serif", letterSpacing: "-0.02em" }}
    >
      {children}
    </h2>
  );
};

const P = ({ children, className = "" }) => (
  <p
    className={`text-[17px] sm:text-[18px] leading-[1.75] text-[#374151] ${className}`}
    style={{ fontFamily: "'DM Sans', sans-serif" }}
  >
    {children}
  </p>
);

const Accent = ({ children }) => (
  <span className="font-semibold" style={{ color: ACCENT }}>
    {children}
  </span>
);

const OurStory = () => {
  const { openBookDemo } = useBookDemo();

  return (
    <div className="min-h-screen bg-white flex flex-col">
      <SEO
        title="Our Story"
        description="Elevante.ai was built to close the AI skilling gap. We're building the AI Learning OS — a unified infrastructure layer that finally makes AI learning experiential, accessible, instructor-led, curriculum-aligned, and scalable for every learner, everywhere."
        keywords="Elevante.ai story, AI Learning OS, applied AI, AI skilling crisis, hands-on AI learning, AI infrastructure for education, mission"
        canonical="https://elevante.ai/our-story"
      />
      <Navbar />

      <main className="flex-1">
        {/* Hero */}
        <section className="pt-[110px] sm:pt-[140px] pb-10 sm:pb-14">
          <div className="max-w-[900px] mx-auto px-6 md:px-10">
            <h1
              className="text-[#0F0F1E] text-[44px] sm:text-[58px] md:text-[72px] font-bold leading-[1.05] tracking-tight text-center"
              style={{ fontFamily: "'DM Sans', sans-serif", letterSpacing: "-0.03em" }}
              data-testid="our-story-h1"
            >
              Our Story
            </h1>
          </div>
        </section>

        {/* Body */}
        <section className="pb-16 sm:pb-24">
          <div className="max-w-[820px] mx-auto px-6 md:px-10 space-y-10">
            <H size="lg">
              <Accent>GenAI has created a powerful illusion.</Accent>
            </H>

            <P>
              Tools like ChatGPT, Groq, Gemini, and DeepSeek make AI feel instant and
              magical — as if the entire field can be mastered through prompts.
            </P>

            <div className="space-y-4">
              <P>But the real world doesn't run on prompts.</P>
              <P>
                It runs on <Accent>data foundations</Accent>,{" "}
                <Accent>classical machine learning</Accent>,{" "}
                <Accent>neural networks</Accent>, <Accent>computer vision</Accent>,{" "}
                <Accent>NLP</Accent>, <Accent>robotics</Accent>,{" "}
                <Accent>GenAI workflows</Accent>, and{" "}
                <Accent>agentic systems</Accent> — skills that demand{" "}
                <em className="text-[#0F0F1E]">structured, experiential learning</em>.
              </P>
            </div>

            <P className="italic text-[#6B7280]">And here's the reality:</P>

            <H size="lg">
              <Accent>
                Academia and industry want applied AI, not more theory — but they're
                missing the infrastructure to deliver it.
              </Accent>
            </H>

            <ul className="space-y-3 pl-1">
              {[
                "No labs.",
                "No GPUs.",
                "No cloud environments.",
                "No curriculum-aligned pathways.",
                "No instructor tooling.",
              ].map((item) => (
                <li
                  key={item}
                  className="flex items-start gap-3 text-[17px] sm:text-[18px] text-[#374151]"
                  style={{ fontFamily: "'DM Sans', sans-serif" }}
                >
                  <span
                    className="mt-[10px] flex-shrink-0 w-[6px] h-[6px] rounded-full"
                    style={{ backgroundColor: ACCENT }}
                  />
                  <span>{item}</span>
                </li>
              ))}
            </ul>

            <P>
              And no way to teach AI without expecting everyone to code from day one.
            </P>

            <div className="space-y-4">
              <P>
                Where such environments <em>do</em> exist, they're typically expensive,
                hard to configure, fragile to maintain, and completely disconnected from
                curriculum or business context.
              </P>
              <P>
                Faculty aren't equipped to build contextual labs. L&D teams can't run
                hands-on programs. Students and professionals get exposure — but not
                capability.
              </P>
            </div>

            <H size="md">
              <Accent>The result?</Accent>
            </H>

            <div className="space-y-4">
              <P>
                Most platforms give videos, PDFs, chatbots, or coding-heavy MOOCs.
              </P>
              <P>
                They produce <Accent>AI spectators, not AI practitioners.</Accent>
              </P>
            </div>

            <P>This misconception is at the heart of today's AI skilling crisis.</P>

            {/* Divider transitioning into the solution */}
            <div className="pt-4">
              <H size="lg">
                This is the gap that led to{" "}
                <span style={{ color: ACCENT }}>elevante.ai</span>.
              </H>
            </div>

            <P>
              We built an <Accent>AI Learning OS</Accent> — a unified infrastructure
              layer that finally makes AI learning <Accent>experiential</Accent>,{" "}
              <Accent>accessible</Accent>, <Accent>instructor-led</Accent>,{" "}
              <Accent>curriculum-aligned</Accent>, and <Accent>scalable</Accent> across
              every discipline and every workforce role.
            </P>

            <P>
              From foundational data skills to frontier GenAI and agentic workflows,{" "}
              <span className="font-semibold text-[#0F0F1E]">elevante.ai</span> turns
              classrooms and training rooms into living AI labs — without the complexity,
              cost, or prerequisites that hold institutions back.
            </P>

            <div className="pt-6">
              <P className="italic text-[#6B7280] text-center">This is our mission:</P>
              <div className="mt-4">
                <H size="lg">
                  <span className="block text-center" style={{ color: ACCENT }}>
                    To democratize applied AI capability at scale — for every learner,
                    everywhere.
                  </span>
                </H>
              </div>
            </div>

            {/* CTA */}
            <div className="pt-10 flex flex-col sm:flex-row items-center justify-center gap-4">
              <button
                type="button"
                onClick={openBookDemo}
                className="inline-flex items-center gap-2 rounded-lg bg-[#8942FE] px-6 py-3 text-[15px] font-bold text-white shadow-sm transition-shadow hover:shadow-md"
                data-testid="our-story-book-demo-btn"
              >
                Book a demo
                <ArrowRight className="w-4 h-4" />
              </button>
              <a
                href="https://app.elevante.ai/login"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 rounded-lg border-[2px] border-[#0F0F1E] px-6 py-3 text-[15px] font-bold text-[#0F0F1E] transition-colors hover:bg-[#0F0F1E] hover:text-white"
                data-testid="our-story-explore-labs-btn"
              >
                Explore AI Labs
              </a>
            </div>
          </div>
        </section>
      </main>

      <Footer />
    </div>
  );
};

export default OurStory;
